import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { jsPDF } from "jspdf";
import { Trash2 } from "lucide-react";
import { formatISTDate, getISTDateInputValue, parseISTTimestamp } from "../utils/dateTime";

const TEMPLATE_IMAGE_URL = "/PrescriptionPad.png";

const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;

const API_URL = "/v1/appointments/online-consult";

const createEmptyPrescriptionItem = () => ({
  medicine: "",
  dose: "",
  frequency: "",
  days: "",
});

const examplePrescriptionItems = [
  { medicine: "Syrup Doxycycline", dose: "0.1 ml", frequency: "once daily", days: "10" },
  { medicine: "Syrup Melonex", dose: "0.01 ml", frequency: "once daily", days: "5" },
  { medicine: "Syrup Respocare", dose: "1 ml + 50 ml water", frequency: "once daily", days: "5" },
  { medicine: "Setgut", dose: "1 pinch", frequency: "once daily", days: "10" },
  { medicine: "Nebulization", dose: "", frequency: "2 times a day with DW", days: "5" },
];

const exampleAdviceItems = [
  "Oral swab test",
  "Fecal examination test",
  "In-person examination",
  "Variety of veggies/pellets diet/mental enrichment",
  "Review after 2 days",
];

const formatNumberedList = (items) =>
  items
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .map((item, index) => `${index + 1}. ${item}`)
    .join("\n");

const formatPrescriptionItem = ({ medicine, dose, frequency, days }) => {
  const details = [medicine.trim(), dose.trim(), frequency.trim()].filter(Boolean);
  const prescription = details.join(" ");

  return `${prescription}${days ? `${prescription ? " " : ""}for ${days} days` : ""}`;
};

const getTodayString = () => {
  return formatISTDate(new Date(), {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const escapeText = (value) => String(value || "").trim();

const slugify = (value) =>
  String(value || "online-consultation")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 40) || "online-consultation";

/*
 * Converts:
 *
 * dd/MM/yyyy -> yyyy-MM-dd
 * yyyy-MM-dd -> yyyy-MM-dd
 *
 * This is important because existing appointments from the backend
 * may already contain yyyy-MM-dd.
 */
const convertDateToApiFormat = (dateString) => {
  if (!dateString) {
    return "";
  }

  const value = String(dateString).trim();

  // Already in API format
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  // UI format: dd/MM/yyyy
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    const [day, month, year] = value.split("/");

    return `${year}-${month}-${day}`;
  }

  return value;
};

/*
 * Converts backend date into the format displayed in the form.
 *
 * yyyy-MM-dd -> dd/MM/yyyy
 * dd/MM/yyyy -> dd/MM/yyyy
 */
const formatDateForDisplay = (dateString) => {
  if (!dateString) {
    return getTodayString();
  }

  const value = String(dateString).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-");

    return `${day}/${month}/${year}`;
  }

  return value;
};

const getConsultationTimestamp = (appointment) => {
  const value = String(
    appointment?.consultationDate || appointment?.date || ""
  ).trim();

  const apiDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (apiDateMatch) {
    return Date.UTC(
      Number(apiDateMatch[1]),
      Number(apiDateMatch[2]) - 1,
      Number(apiDateMatch[3])
    );
  }

  const displayDateMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (displayDateMatch) {
    return Date.UTC(
      Number(displayDateMatch[3]),
      Number(displayDateMatch[2]) - 1,
      Number(displayDateMatch[1])
    );
  }

  const timestamp = parseISTTimestamp(value);
  return Number.isFinite(timestamp) ? timestamp : null;
};

const loadImageDataUrl = async (src) => {
  const response = await fetch(src);

  if (!response.ok) {
    throw new Error("Unable to load consultation pad template");
  }

  const blob = await response.blob();

  return await new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;

    reader.readAsDataURL(blob);
  });
};

const drawLabelValue = (pdf, label, value, x, y, options = {}) => {
  const {
    labelFontSize = 10,
    valueFontSize = 12,
    labelColor = [55, 65, 81],
    valueColor = [17, 24, 39],
    maxWidth = 180,
  } = options;

  pdf.setTextColor(...labelColor);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(labelFontSize);

  pdf.text(String(label || "").toUpperCase(), x, y);

  pdf.setTextColor(...valueColor);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(valueFontSize);

  const lines = pdf.splitTextToSize(escapeText(value) || "-", maxWidth);

  pdf.text(lines, x, y + 14);
};

const drawSection = (pdf, title, text, x, y, width) => {
  const safeText = escapeText(text);

  if (!safeText) {
    return y;
  }

  pdf.setTextColor(0, 0, 0);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);

  pdf.text(`${title}:`, x, y);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(11);

  const lines = pdf.splitTextToSize(safeText, width);

  pdf.text(lines, x, y + 14);

  return y + 14 + lines.length * 13 + 8;
};

const buildPdf = async (form, clinic) => {
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "a4",
  });

  const bg = await loadImageDataUrl(TEMPLATE_IMAGE_URL);

  pdf.addImage(bg, "PNG", 0, 0, PAGE_WIDTH, PAGE_HEIGHT);

  /*
   * HEADER
   */

  drawLabelValue(pdf, "Pet Name", form.petName, 60, 160, {
    maxWidth: 145,
  });

  drawLabelValue(pdf, "Weight", form.weight, 290, 160, {
    maxWidth: 110,
  });

  drawLabelValue(pdf, "Date", form.date, 425, 160, {
    maxWidth: 120,
  });

  drawLabelValue(pdf, "Mobile", form.mobile, 60, 190, {
    maxWidth: 190,
  });

  drawLabelValue(pdf, "Age", form.age, 60, 220, {
    maxWidth: 190,
  });

  drawLabelValue(pdf, "Species", form.species, 290, 190, {
    maxWidth: 110,
  });

  drawLabelValue(pdf, "Breed", form.breed, 425, 190, {
    maxWidth: 150,
  });

  /*
   * BODY
   */

  let currentY = 280;

  currentY = drawSection(pdf, "Problem", form.problem, 42, currentY, 510);

  if (escapeText(form.previousDetails)) {
    currentY = drawSection(
      pdf,
      "Previous Prescription / Diagnostic Details",
      form.previousDetails,
      42,
      currentY + 16,
      510
    );
  }

  currentY = drawSection(
    pdf,
    "Prescription",
    form.prescription,
    42,
    currentY + 16,
    510
  );

  drawSection(
    pdf,
    "Advice",
    form.advice,
    42,
    Math.max(currentY + 16, 490),
    510
  );

  const fileName = `online-consultation-${slugify(form.petName)}-${getISTDateInputValue()}.pdf`;

  pdf.save(fileName);
};

function OnlineConsultationModal({
  show,
  onClose,
  clinic,
  speciesList = [],
  breedOptions = {},
}) {
  /*
   * ---------------------------------------------------------
   * FORM STATE
   * ---------------------------------------------------------
   */

  const getInitialForm = () => ({
    petName: "",
    age: "",
    mobile: "",
    weight: "",
    species: "",
    breed: "",
    date: getTodayString(),
    problem: "",
    previousDetails: "",
    prescription: "",
    advice: "",
  });

  const [form, setForm] = useState(getInitialForm());
  const [prescriptionItems, setPrescriptionItems] = useState([]);
  const [adviceItems, setAdviceItems] = useState([""]);
  const [builderMessage, setBuilderMessage] = useState("");
  const [showPrescriptionBuilder, setShowPrescriptionBuilder] = useState(false);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  /*
   * ---------------------------------------------------------
   * TABS
   * ---------------------------------------------------------
   */

  const [activeTab, setActiveTab] = useState("new");

  /*
   * ---------------------------------------------------------
   * EXISTING APPOINTMENTS
   * ---------------------------------------------------------
   */

  const [appointments, setAppointments] = useState([]);

  const [appointmentsLoading, setAppointmentsLoading] = useState(false);

  const [appointmentsError, setAppointmentsError] = useState("");
  const sortedAppointments = useMemo(
    () =>
      appointments
        .map((appointment, index) => ({
          appointment,
          index,
          timestamp: getConsultationTimestamp(appointment),
        }))
        .sort((a, b) => {
          if (a.timestamp === null && b.timestamp === null) return a.index - b.index;
          if (a.timestamp === null) return 1;
          if (b.timestamp === null) return -1;
          return b.timestamp - a.timestamp || a.index - b.index;
        })
        .map(({ appointment }) => appointment),
    [appointments]
  );

  /*
   * ---------------------------------------------------------
   * LOAD EXISTING APPOINTMENTS
   * ---------------------------------------------------------
   */

  const loadExistingAppointments = async () => {
    try {
      setAppointmentsLoading(true);
      setAppointmentsError("");

      const response = await fetch(API_URL, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      let data = null;

      try {
        data = await response.json();
      } catch (jsonError) {
        throw new Error("Invalid response received from server");
      }

      if (!response.ok) {
        throw new Error(
          data?.message || "Unable to load existing appointments"
        );
      }

      /*
       * Backend may return:
       *
       * [
       *   {...}
       * ]
       *
       * OR
       *
       * {
       *   appointments: [...]
       * }
       *
       * OR
       *
       * {
       *   consultationList: [...]
       * }
       */

      let list = [];

      if (Array.isArray(data)) {
        list = data;
      } else if (Array.isArray(data?.consultationList)) {
        list = data.consultationList;
      } else if (Array.isArray(data?.appointments)) {
        list = data.appointments;
      } else if (Array.isArray(data?.data)) {
        list = data.data;
      }

      setAppointments(list);
    } catch (err) {
      console.error("Unable to load appointments:", err);

      setAppointments([]);

      setAppointmentsError(
        err.message || "Unable to load existing appointments"
      );
    } finally {
      setAppointmentsLoading(false);
    }
  };

  /*
   * ---------------------------------------------------------
   * TAB CHANGE
   * ---------------------------------------------------------
   */

  const handleTabChange = (tab) => {
    setActiveTab(tab);

    if (tab === "existing") {
      loadExistingAppointments();
    }
  };

  const handleClearForm = () => {
    setForm({
      petName: "",
      age: "",
      mobile: "",
      weight: "",
      species: "",
      breed: "",
      date: getTodayString(),
      problem: "",
      previousDetails: "",
      prescription: "",
      advice: "",
    });

    setPrescriptionItems([]);
    setAdviceItems([""]);
    setBuilderMessage("");
    setShowPrescriptionBuilder(false);
    setErrors({});
  };

  /*
   * ---------------------------------------------------------
   * RESET WHEN MODAL OPENS
   * ---------------------------------------------------------
   */

  useEffect(() => {
    if (!show) {
      return;
    }

    setForm(getInitialForm());
    setPrescriptionItems([]);
    setAdviceItems([""]);
    setBuilderMessage("");
    setShowPrescriptionBuilder(false);
    setErrors({});
    setLoading(false);
    setSelectedAppointment(null);
  }, [show]);

  /*
   * ---------------------------------------------------------
   * BREED LIST
   * ---------------------------------------------------------
   */

  const breedList = useMemo(
    () => breedOptions?.[form.species] || [],
    [breedOptions, form.species]
  );

  const handleViewAppointment = (appointment) => {
    setSelectedAppointment(appointment);
  };

  /*
   * ---------------------------------------------------------
   * KEEP BREED VALID
   * ---------------------------------------------------------
   */

  useEffect(() => {
    if (
      form.species &&
      form.breed &&
      breedList.length > 0 &&
      !breedList.includes(form.breed)
    ) {
      setForm((prev) => ({
        ...prev,
        breed: "",
      }));
    }
  }, [breedList, form.breed, form.species]);

  /*
   * ---------------------------------------------------------
   * HANDLE FORM CHANGE
   * ---------------------------------------------------------
   */

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
      download: "",
    }));
  };

  const applyPrescriptionItems = () => {
    const prescription = formatNumberedList(
      prescriptionItems.map(formatPrescriptionItem)
    );

    if (!prescription) {
      setBuilderMessage("Enter at least one medicine or treatment before applying.");
      return;
    }

    setForm((prev) => ({ ...prev, prescription }));
    setErrors((prev) => ({ ...prev, prescription: "" }));
    setBuilderMessage("");
  };

  const applyAdviceItems = () => {
    const advice = formatNumberedList(adviceItems);

    if (!advice) {
      setBuilderMessage("Enter at least one advice item before applying.");
      return;
    }

    setForm((prev) => ({ ...prev, advice }));
    setErrors((prev) => ({ ...prev, advice: "" }));
    setBuilderMessage("");
  };

  const loadExampleTemplate = () => {
    setPrescriptionItems(examplePrescriptionItems.map((item) => ({ ...item })));
    setAdviceItems([...exampleAdviceItems]);
    setForm((prev) => ({
      ...prev,
      prescription: formatNumberedList(examplePrescriptionItems.map(formatPrescriptionItem)),
      advice: formatNumberedList(exampleAdviceItems),
    }));
    setErrors((prev) => ({ ...prev, prescription: "", advice: "" }));
    setBuilderMessage("Example loaded. Review and edit the details before saving.");
  };

  /*
   * ---------------------------------------------------------
   * SAVE CONSULTATION
   * ---------------------------------------------------------
   */

  const saveConsultation = async () => {
    const payload = {
      petName: form.petName,
      age: form.age,
      mobile: form.mobile,
      weight: form.weight,
      species: form.species,
      breed: form.breed,

      consultationDate: convertDateToApiFormat(form.date),

      problem: form.problem,

      previousDetails: form.previousDetails,

      prescription: form.prescription,

      advice: form.advice,
    };

    console.log("Saving consultation payload:", payload);

    const response = await fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    let data = null;

    try {
      data = await response.json();
    } catch (jsonError) {
      throw new Error("Invalid response received while saving consultation");
    }

    if (!response.ok || !data?.success) {
      throw new Error(data?.message || "Unable to save consultation");
    }

    return data;
  };


  const validate = () => {
    const next = {};

    if (!form.petName.trim()) {
      next.petName = "Pet name is required";
    }

    if (!form.age.trim()) {
      next.age = "Age is required";
    }

    if (!form.mobile.trim()) {
      next.mobile = "Mobile number is required";
    }

    if (!form.weight.trim()) {
      next.weight = "Weight is required";
    }

    if (!form.species.trim()) {
      next.species = "Species is required";
    }

    if (!form.breed.trim()) {
      next.breed = "Breed is required";
    }

    if (!form.problem.trim()) {
      next.problem = "Problem description is required";
    }

    /*
     * previousDetails is OPTIONAL.
     */

    if (!form.prescription.trim()) {
      next.prescription = "Prescription is required";
    }

    if (!form.advice.trim()) {
      next.advice = "Advice is required";
    }

    return next;
  };

  /*
   * ---------------------------------------------------------
   * DOWNLOAD PRESCRIPTION
   * ---------------------------------------------------------
   */

  const handleDownload = async () => {
    const nextErrors = validate();

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    try {
      setLoading(true);

      /*
       * STEP 1:
       * Save consultation into PostgreSQL.
       */

      const savedConsultation = await saveConsultation();

      console.log("Consultation saved:", savedConsultation?.consultationId);

      /*
       * STEP 2:
       * Generate PDF only after successful save.
       */

      await buildPdf(form, clinic);
    } catch (err) {
      console.error("Unable to save consultation:", err);

      setErrors((prev) => ({
        ...prev,
        download:
          err.message ||
          "Unable to save consultation or generate prescription PDF",
      }));
    } finally {
      setLoading(false);
    }
  };

  /*
   * ---------------------------------------------------------
   * STYLES
   * ---------------------------------------------------------
   */

  const topLabelStyle = {
    position: "absolute",
    fontSize: "13px",
    fontWeight: 600,
    color: "#111827",
    textShadow: "0 1px 0 rgba(255,255,255,0.9)",
  };

  const topValueStyle = {
    position: "absolute",
    fontSize: "13px",
    fontWeight: 700,
    color: "#111827",
    textShadow: "0 1px 0 rgba(255,255,255,0.85)",
  };

  /*
   * ---------------------------------------------------------
   * MODAL
   * ---------------------------------------------------------
   */

  if (!show) {
    return null;
  }

  return (
    <>
      <div className="modal-backdrop fade show"></div>

      <div
        className="modal show fade d-block"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog modal-xl modal-dialog-scrollable">
          <div className="modal-content">
            {/* ================================================= */}
            {/* HEADER */}
            {/* ================================================= */}

            <div className="modal-header">
              <h5 className="modal-title">Online Consultation</h5>

              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                disabled={loading}
                aria-label="Close"
              ></button>
            </div>

            {/* ================================================= */}
            {/* TABS */}
            {/* ================================================= */}

            <div className="px-3 pt-2 border-bottom">
              <ul className="nav nav-tabs">
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link ${
                      activeTab === "new" ? "active" : ""
                    }`}
                    onClick={() => handleTabChange("new")}
                  >
                    New Consultation
                  </button>
                </li>

                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link ${
                      activeTab === "existing" ? "active" : ""
                    }`}
                    onClick={() => handleTabChange("existing")}
                  >
                    Existing Appointments
                  </button>
                </li>
              </ul>
            </div>

            {/* ================================================= */}
            {/* NEW CONSULTATION TAB */}
            {/* ================================================= */}

            {activeTab === "new" && (
              <div className="modal-body">
                <div className="row g-3">
                  {/* ================================================= */}
                  {/* FORM */}
                  {/* ================================================= */}

                  <div className="col-lg-5">
                    <div
                      className="card border-0 shadow-sm p-3 h-100"
                      style={{
                        background: "linear-gradient(180deg, #f7fbff, #eef4ff)",
                      }}
                    >
                      <h6 className="mb-3 fw-bold">Consultation Form</h6>

                      <div className="row g-2">
                        {/* PET NAME */}

                        <div className="col-md-6">
                          <label className="form-label small mb-1">
                            Pet Name
                          </label>

                          <input
                            type="text"
                            className="form-control"
                            name="petName"
                            value={form.petName}
                            onChange={handleChange}
                          />

                          {errors.petName && (
                            <small className="text-danger">
                              {errors.petName}
                            </small>
                          )}
                        </div>

                        {/* AGE */}

                        <div className="col-md-6">
                          <label className="form-label small mb-1">Age</label>

                          <input
                            type="text"
                            className="form-control"
                            name="age"
                            value={form.age}
                            onChange={handleChange}
                            placeholder="e.g. 2 Years"
                          />

                          {errors.age && (
                            <small className="text-danger">{errors.age}</small>
                          )}
                        </div>

                        {/* MOBILE */}

                        <div className="col-md-6">
                          <label className="form-label small mb-1">
                            Mobile
                          </label>

                          <input
                            type="text"
                            className="form-control"
                            name="mobile"
                            value={form.mobile}
                            onChange={handleChange}
                          />

                          {errors.mobile && (
                            <small className="text-danger">
                              {errors.mobile}
                            </small>
                          )}
                        </div>

                        {/* WEIGHT */}

                        <div className="col-md-6">
                          <label className="form-label small mb-1">
                            Weight
                          </label>

                          <input
                            type="text"
                            className="form-control"
                            name="weight"
                            value={form.weight}
                            onChange={handleChange}
                          />

                          {errors.weight && (
                            <small className="text-danger">
                              {errors.weight}
                            </small>
                          )}
                        </div>

                        {/* DATE */}

                        <div className="col-md-6">
                          <label className="form-label small mb-1">Date</label>

                          <input
                            type="text"
                            className="form-control"
                            name="date"
                            value={form.date}
                            readOnly
                          />
                        </div>

                        {/* SPECIES */}

                        <div className="col-md-6">
                          <label className="form-label small mb-1">
                            Species
                          </label>

                          <select
                            className="form-select"
                            name="species"
                            value={form.species}
                            onChange={handleChange}
                          >
                            <option value="">Select Species</option>

                            {speciesList.map((species) => (
                              <option key={species} value={species}>
                                {species}
                              </option>
                            ))}
                          </select>

                          {errors.species && (
                            <small className="text-danger">
                              {errors.species}
                            </small>
                          )}
                        </div>

                        {/* BREED */}

                        <div className="col-md-6">
                          <label className="form-label small mb-1">Breed</label>

                          <select
                            className="form-select"
                            name="breed"
                            value={form.breed}
                            onChange={handleChange}
                          >
                            <option value="">Select Breed</option>

                            {breedList.map((breed) => (
                              <option key={breed} value={breed}>
                                {breed}
                              </option>
                            ))}
                          </select>

                          {errors.breed && (
                            <small className="text-danger">
                              {errors.breed}
                            </small>
                          )}
                        </div>

                        {/* PROBLEM */}

                        <div className="col-12">
                          <label className="form-label small mb-1">
                            Problem
                          </label>

                          <textarea
                            className="form-control"
                            rows="3"
                            name="problem"
                            value={form.problem}
                            onChange={handleChange}
                            placeholder="Describe the current problem..."
                          />

                          {errors.problem && (
                            <small className="text-danger">
                              {errors.problem}
                            </small>
                          )}
                        </div>

                        {/* PREVIOUS DETAILS */}

                        <div className="col-12">
                          <label className="form-label small mb-1">
                            Previous Prescription / Diagnostic Details
                            <span className="text-muted ms-1">(Optional)</span>
                          </label>

                          <textarea
                            className="form-control"
                            rows="4"
                            name="previousDetails"
                            value={form.previousDetails}
                            onChange={handleChange}
                            placeholder="Enter previous prescription, diagnostic reports, test results, previous medications, treatment history, etc."
                          />

                          <small className="text-muted">
                            Add any previous treatment or diagnostic information
                            relevant to this consultation.
                          </small>
                        </div>

                        {/* PRESCRIPTION */}

                        <div className="col-12">
                          <div className="mb-2">
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => {
                                setBuilderMessage("");
                                setShowPrescriptionBuilder(true);
                              }}
                            >
                              Open prescription &amp; advice builder
                            </button>
                          </div>

                          {showPrescriptionBuilder && createPortal(
                            <>
                              <div
                                className="modal-backdrop fade show"
                                style={{ zIndex: 1060 }}
                                onClick={() => setShowPrescriptionBuilder(false)}
                              ></div>
                              <div
                                className="modal show d-block"
                                role="dialog"
                                aria-modal="true"
                                aria-labelledby="prescription-builder-title"
                                style={{ zIndex: 1065 }}
                              >
                                <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                                  <div className="modal-content">
                                    <div className="modal-header">
                                      <h5 className="modal-title" id="prescription-builder-title">
                                        Prescription &amp; advice builder
                                      </h5>
                                      <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close builder"
                                        onClick={() => setShowPrescriptionBuilder(false)}
                                      ></button>
                                    </div>
                                    <div className="modal-body">
                              <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-2">
                                <span className="fw-semibold small">Medicines and treatments</span>
                                <div className="d-flex gap-2 flex-wrap">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={loadExampleTemplate}
                                  >
                                    Load provided example
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => setPrescriptionItems((items) => [...items, createEmptyPrescriptionItem()])}
                                  >
                                    Add medicine
                                  </button>
                                </div>
                              </div>

                              {prescriptionItems.map((item, index) => (
                                <div className="row g-2 align-items-end mb-2" key={`prescription-${index}`}>
                                  <div className="col-md-4">
                                    <label className="form-label small mb-1" htmlFor={`medicine-${index}`}>Medicine / treatment</label>
                                    <input
                                      id={`medicine-${index}`}
                                      className="form-control form-control-sm"
                                      value={item.medicine}
                                      onChange={(event) => setPrescriptionItems((items) =>
                                        items.map((entry, itemIndex) => itemIndex === index
                                          ? { ...entry, medicine: event.target.value }
                                          : entry
                                        )
                                      )}
                                      placeholder="e.g. Syrup name"
                                    />
                                  </div>
                                  <div className="col-md-3">
                                    <label className="form-label small mb-1" htmlFor={`dose-${index}`}>Dose / amount</label>
                                    <input
                                      id={`dose-${index}`}
                                      className="form-control form-control-sm"
                                      value={item.dose}
                                      onChange={(event) => setPrescriptionItems((items) =>
                                        items.map((entry, itemIndex) => itemIndex === index
                                          ? { ...entry, dose: event.target.value }
                                          : entry
                                        )
                                      )}
                                      placeholder="e.g. 0.1 ml"
                                    />
                                  </div>
                                  <div className="col-md-3">
                                    <label className="form-label small mb-1" htmlFor={`frequency-${index}`}>Frequency / instructions</label>
                                    <input
                                      id={`frequency-${index}`}
                                      className="form-control form-control-sm"
                                      value={item.frequency}
                                      onChange={(event) => setPrescriptionItems((items) =>
                                        items.map((entry, itemIndex) => itemIndex === index
                                          ? { ...entry, frequency: event.target.value }
                                          : entry
                                        )
                                      )}
                                      placeholder="e.g. once daily"
                                    />
                                  </div>
                                  <div className="col-md-2">
                                    <label className="form-label small mb-1" htmlFor={`days-${index}`}>Days</label>
                                    <div className="input-group input-group-sm">
                                      <input
                                        id={`days-${index}`}
                                        type="number"
                                        min="1"
                                        className="form-control"
                                        value={item.days}
                                        onChange={(event) => setPrescriptionItems((items) =>
                                          items.map((entry, itemIndex) => itemIndex === index
                                            ? { ...entry, days: event.target.value }
                                            : entry
                                          )
                                        )}
                                        placeholder="e.g. 5"
                                      />
                                      <button
                                        type="button"
                                        className="btn btn-outline-danger"
                                        aria-label={`Remove medicine ${index + 1}`}
                                        onClick={() => setPrescriptionItems((items) =>
                                          items.filter((_, itemIndex) => itemIndex !== index)
                                        )}
                                      >
                                        <Trash2 size={16} aria-hidden="true" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}

                              <button
                                type="button"
                                className="btn btn-sm btn-primary mt-1"
                                onClick={applyPrescriptionItems}
                                disabled={prescriptionItems.length === 0}
                              >
                                Fill prescription
                              </button>

                              <hr />

                              <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-2">
                                <span className="fw-semibold small">Advice</span>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => setAdviceItems((items) => [...items, ""])}
                                >
                                  Add advice
                                </button>
                              </div>

                              {adviceItems.map((advice, index) => (
                                <div className="input-group input-group-sm mb-2" key={`advice-${index}`}>
                                  <label className="input-group-text" htmlFor={`advice-${index}`}>{index + 1}</label>
                                  <input
                                    id={`advice-${index}`}
                                    className="form-control"
                                    value={advice}
                                    onChange={(event) => setAdviceItems((items) =>
                                      items.map((entry, itemIndex) => itemIndex === index
                                        ? event.target.value
                                        : entry
                                      )
                                    )}
                                    placeholder="Enter advice or test"
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger"
                                    aria-label={`Remove advice ${index + 1}`}
                                    onClick={() => setAdviceItems((items) =>
                                      items.filter((_, itemIndex) => itemIndex !== index)
                                    )}
                                  >
                                    <Trash2 size={16} aria-hidden="true" />
                                  </button>
                                </div>
                              ))}

                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={applyAdviceItems}
                                disabled={adviceItems.length === 0}
                              >
                                Fill advice
                              </button>

                              {builderMessage && (
                                <div className="small text-muted mt-2" role="status">
                                  {builderMessage}
                                </div>
                              )}
                            </div>
                                    <div className="modal-footer">
                                      <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowPrescriptionBuilder(false)}
                                      >
                                        Done
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </>,
                            document.body
                          )}

                          <label className="form-label small mb-1 d-block">
                            Prescription
                          </label>

                          <textarea
                            className="form-control"
                            rows="4"
                            name="prescription"
                            value={form.prescription}
                            onChange={handleChange}
                            placeholder="Enter current prescription..."
                          />

                          {errors.prescription && (
                            <small className="text-danger">
                              {errors.prescription}
                            </small>
                          )}
                        </div>

                        {/* ADVICE */}

                        <div className="col-12">
                          <label className="form-label small mb-1">
                            Advice
                          </label>

                          <textarea
                            className="form-control"
                            rows="3"
                            name="advice"
                            value={form.advice}
                            onChange={handleChange}
                          />

                          {errors.advice && (
                            <small className="text-danger">
                              {errors.advice}
                            </small>
                          )}
                        </div>
                      </div>

                      {/* ERROR */}

                      {errors.download && (
                        <div className="alert alert-danger mt-3 mb-0 py-2">
                          {errors.download}
                        </div>
                      )}

                      {/* BUTTONS */}

                      <div className="d-flex gap-2 mt-3 flex-wrap">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleDownload}
                          disabled={loading}
                        >
                          {loading ? "Generating..." : "Download Prescription"}
                        </button>

                        <button
                          type="button"
                          className="btn btn-outline-warning"
                          onClick={handleClearForm}
                          disabled={loading}
                        >
                          Clear Form
                        </button>

                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={onClose}
                          disabled={loading}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* ================================================= */}
                  {/* PREVIEW */}
                  {/* ================================================= */}

                  <div className="col-lg-7">
                    <div
                      className="card border-0 shadow-sm p-3 h-100"
                      style={{
                        background: "#fbfcff",
                      }}
                    >
                      <h6 className="mb-3 fw-bold">Live Preview</h6>

                      <div
                        className="position-relative w-100 overflow-hidden"
                        style={{
                          aspectRatio: "210 / 297",
                          backgroundImage: `url(${TEMPLATE_IMAGE_URL})`,
                          backgroundRepeat: "no-repeat",
                          backgroundSize: "cover",
                          backgroundPosition: "center top",
                          border: "1px solid #d7dde8",
                          borderRadius: "14px",
                        }}
                      >
                        {/* HEADER DETAILS */}

                        <div
                          style={{
                            ...topLabelStyle,
                            left: "17.5%",
                            top: "16.0%",
                          }}
                        >
                          Pet Name:
                        </div>

                        <div
                          style={{
                            ...topValueStyle,
                            left: "17.5%",
                            top: "17.4%",
                          }}
                        >
                          {form.petName || "-"}
                        </div>

                        <div
                          style={{
                            ...topLabelStyle,
                            left: "45.8%",
                            top: "16.0%",
                          }}
                        >
                          Weight:
                        </div>

                        <div
                          style={{
                            ...topValueStyle,
                            left: "45.8%",
                            top: "17.4%",
                          }}
                        >
                          {form.weight || "-"}
                        </div>

                        <div
                          style={{
                            ...topLabelStyle,
                            left: "68.5%",
                            top: "16.0%",
                          }}
                        >
                          Date:
                        </div>

                        <div
                          style={{
                            ...topValueStyle,
                            left: "68.5%",
                            top: "17.4%",
                          }}
                        >
                          {form.date || "-"}
                        </div>

                        <div
                          style={{
                            ...topLabelStyle,
                            left: "17.5%",
                            top: "19.8%",
                          }}
                        >
                          Mobile:
                        </div>

                        <div
                          style={{
                            ...topValueStyle,
                            left: "17.5%",
                            top: "21.2%",
                          }}
                        >
                          {form.mobile || "-"}
                        </div>

                        <div
                          style={{
                            ...topLabelStyle,
                            left: "17.5%",
                            top: "23.6%",
                          }}
                        >
                          Age:
                        </div>

                        <div
                          style={{
                            ...topValueStyle,
                            left: "17.5%",
                            top: "25.0%",
                          }}
                        >
                          {form.age || "-"}
                        </div>

                        <div
                          style={{
                            ...topLabelStyle,
                            left: "45.8%",
                            top: "19.8%",
                          }}
                        >
                          Species:
                        </div>

                        <div
                          style={{
                            ...topValueStyle,
                            left: "45.8%",
                            top: "21.2%",
                          }}
                        >
                          {form.species || "-"}
                        </div>

                        <div
                          style={{
                            ...topLabelStyle,
                            left: "68.5%",
                            top: "19.8%",
                          }}
                        >
                          Breed:
                        </div>

                        <div
                          style={{
                            ...topValueStyle,
                            left: "68.5%",
                            top: "21.2%",
                          }}
                        >
                          {form.breed || "-"}
                        </div>

                        {/* BODY */}

                        <div
                          style={{
                            position: "absolute",
                            left: "4.5%",
                            top: "27.0%",
                            width: "90%",
                            color: "#111827",
                            fontSize: "11.5px",
                            lineHeight: 1.45,
                          }}
                        >
                          {/* PROBLEM */}

                          <div
                            style={{
                              marginBottom: "10px",
                            }}
                          >
                            <div
                              style={{
                                fontWeight: 700,
                                marginBottom: "4px",
                              }}
                            >
                              Problem
                            </div>

                            <div
                              style={{
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {form.problem || "-"}
                            </div>
                          </div>

                          {/* PREVIOUS DETAILS */}

                          {form.previousDetails && (
                            <div
                              style={{
                                marginBottom: "10px",
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: 700,
                                  marginBottom: "4px",
                                }}
                              >
                                Previous Prescription / Diagnostic Details
                              </div>

                              <div
                                style={{
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                {form.previousDetails}
                              </div>
                            </div>
                          )}

                          {/* PRESCRIPTION */}

                          <div
                            style={{
                              marginBottom: "10px",
                            }}
                          >
                            <div
                              style={{
                                fontWeight: 700,
                                marginBottom: "4px",
                              }}
                            >
                              Prescription
                            </div>

                            <div
                              style={{
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {form.prescription || "-"}
                            </div>
                          </div>

                          {/* ADVICE */}

                          <div>
                            <div
                              style={{
                                fontWeight: 700,
                                marginBottom: "4px",
                              }}
                            >
                              Advice
                            </div>

                            <div
                              style={{
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {form.advice || "-"}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-2 text-muted small">
                        This preview follows the clinic prescription-pad
                        template and keeps the same downloadable format.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ================================================= */}
            {/* EXISTING APPOINTMENTS TAB */}
            {/* ================================================= */}

            {activeTab === "existing" && (
              <div className="modal-body">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    {/* HEADER */}

                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="fw-bold mb-0">Existing Appointments</h6>

                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={loadExistingAppointments}
                        disabled={appointmentsLoading}
                      >
                        {appointmentsLoading ? "Loading..." : "Refresh"}
                      </button>
                    </div>

                    {/* ERROR */}

                    {appointmentsError && (
                      <div className="alert alert-danger">
                        {appointmentsError}
                      </div>
                    )}

                    {/* LOADING */}

                    {appointmentsLoading ? (
                      <div className="text-center py-5">
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>

                        <div className="mt-2 text-muted">
                          Loading appointments...
                        </div>
                      </div>
                    ) : sortedAppointments.length === 0 ? (
                      <div className="text-center text-muted py-5">
                        No appointments found.
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-hover align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>Pet Name</th>

                              <th>Mobile</th>

                              <th>Species</th>

                              <th>Breed</th>

                              <th>Date</th>

                              <th>Problem</th>

                              <th>Action</th>
                            </tr>
                          </thead>

                          <tbody>
                            {sortedAppointments.map((appointment, index) => {
                              /*
                               * Create a reliable key.
                               */

                              const appointmentKey =
                                appointment?.id ??
                                appointment?.appointmentId ??
                                appointment?.consultationId ??
                                index;
 

                              return (
                                <tr key={appointmentKey}>
                                  {/* PET */}

                                  <td className="fw-semibold">
                                    {appointment?.petName || "-"}
                                  </td>

                                  {/* MOBILE */}

                                  <td>{appointment?.mobile || "-"}</td>

                                  {/* SPECIES */}

                                  <td>{appointment?.species || "-"}</td>

                                  {/* BREED */}

                                  <td>{appointment?.breed || "-"}</td>

                                  {/* DATE */}

                                  <td>
                                    {formatDateForDisplay(
                                      appointment?.consultationDate ||
                                        appointment?.date
                                    )}
                                  </td>

                                  {/* PROBLEM */}

                                  <td>
                                    <div
                                      style={{
                                        maxWidth: "300px",
                                        whiteSpace: "pre-wrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                    >
                                      {appointment?.problem || "-"}
                                    </div>
                                  </td>

                                  {/* ACTION */}

                                  <td>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary"
                                      onClick={() =>
                                        handleViewAppointment(appointment)
                                      }
                                      disabled={loading}
                                    >
                                      View
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedAppointment && (
        <>
          <div className="modal-backdrop fade show"></div>

          <div
            className="modal show fade d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Consultation Details</h5>

                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setSelectedAppointment(null)}
                  ></button>
                </div>

                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="small text-muted">Pet Name</div>
                      <div className="fw-semibold">
                        {selectedAppointment.petName || "-"}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="small text-muted">Consultation Date</div>
                      <div className="fw-semibold">
                        {formatDateForDisplay(
                          selectedAppointment.consultationDate ||
                            selectedAppointment.date
                        )}
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="small text-muted">Age</div>
                      <div className="fw-semibold">
                        {selectedAppointment.age || "-"}
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="small text-muted">Weight</div>
                      <div className="fw-semibold">
                        {selectedAppointment.weight || "-"}
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="small text-muted">Mobile</div>
                      <div className="fw-semibold">
                        {selectedAppointment.mobile || "-"}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="small text-muted">Species</div>
                      <div className="fw-semibold">
                        {selectedAppointment.species || "-"}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="small text-muted">Breed</div>
                      <div className="fw-semibold">
                        {selectedAppointment.breed || "-"}
                      </div>
                    </div>

                    <div className="col-12">
                      <hr />
                    </div>

                    <div className="col-12">
                      <div className="small text-muted mb-1">Problem</div>

                      <div
                        className="p-3 bg-light rounded"
                        style={{
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {selectedAppointment.problem || "-"}
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="small text-muted mb-1">
                        Previous Prescription / Diagnostic Details
                      </div>

                      <div
                        className="p-3 bg-light rounded"
                        style={{
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {selectedAppointment.previousDetails || "-"}
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="small text-muted mb-1">Prescription</div>

                      <div
                        className="p-3 bg-light rounded"
                        style={{
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {selectedAppointment.prescription || "-"}
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="small text-muted mb-1">Advice</div>

                      <div
                        className="p-3 bg-light rounded"
                        style={{
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {selectedAppointment.advice || "-"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setSelectedAppointment(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default OnlineConsultationModal;
